import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Clock, Users } from "lucide-react";
import { useState } from "react";

const mockPolls = [
  {
    id: 1,
    question: "What excites you most about Tesla's 2025 roadmap?",
    type: "instant",
    status: "active",
    closesIn: "2 days",
    totalVotes: 8340,
    options: [
      { text: "Semi production scaling", votes: 2480, percentage: 30 },
      { text: "New vehicle announcements", votes: 2840, percentage: 34 },
      { text: "Energy products", votes: 1920, percentage: 23 },
      { text: "Software improvements", votes: 1100, percentage: 13 },
    ],
    demographics: {
      region: [
        { label: "North America", value: 45 },
        { label: "Europe", value: 30 },
        { label: "Asia", value: 25 },
      ],
      persona: [
        { label: "Engineers", value: 52 },
        { label: "Investors", value: 28 },
        { label: "Fans", value: 20 },
      ],
    },
    voted: false,
  },
  {
    id: 2,
    question: "When will we see the first Starship orbital refueling?",
    type: "scheduled",
    status: "upcoming",
    startsIn: "Starts in 3 days",
    totalVotes: 0,
    options: [
      { text: "Q1 2025", votes: 0, percentage: 0 },
      { text: "Q2 2025", votes: 0, percentage: 0 },
      { text: "Q3 2025", votes: 0, percentage: 0 },
      { text: "Later than Q3", votes: 0, percentage: 0 },
    ],
    demographics: { region: [], persona: [] },
    voted: false,
  },
  {
    id: 3,
    question: "Should Neuralink prioritize medical or consumer applications?",
    type: "instant",
    status: "closed",
    closesIn: "Closed",
    totalVotes: 12450,
    options: [
      { text: "Medical first", votes: 7230, percentage: 58 },
      { text: "Consumer first", votes: 3640, percentage: 29 },
      { text: "Simultaneous", votes: 1580, percentage: 13 },
    ],
    demographics: {
      region: [
        { label: "North America", value: 50 },
        { label: "Europe", value: 35 },
        { label: "Asia", value: 15 },
      ],
      persona: [
        { label: "Medical Pros", value: 67 },
        { label: "Investors", value: 23 },
        { label: "General Public", value: 10 },
      ],
    },
    voted: true,
  },
];

export default function Polls() {
  const [polls, setPolls] = useState(mockPolls);
  const [selectedPoll, setSelectedPoll] = useState<number | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const handleVote = (pollId: number, optionIndex: number) => {
    setPolls(polls.map(p => {
      if (p.id === pollId && !p.voted) {
        const updatedOptions = [...p.options];
        updatedOptions[optionIndex] = {
          ...updatedOptions[optionIndex],
          votes: updatedOptions[optionIndex].votes + 1,
        };
        const newTotal = updatedOptions.reduce((sum, opt) => sum + opt.votes, 0);
        updatedOptions.forEach(opt => {
          opt.percentage = Math.round((opt.votes / newTotal) * 100);
        });
        return { ...p, options: updatedOptions, totalVotes: newTotal, voted: true };
      }
      return p;
    }));
  };

  return (
    <div className="space-y-8">
      {/* Poll Creator */}
      <motion.div variants={itemVariants} className="border border-border p-6">
        <h3 className="text-lg font-medium mb-4">Create a Poll</h3>
        <div className="space-y-4">
          <Input
            placeholder="What's your question?"
            className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
          />
          <div className="flex gap-3">
            <Button variant="outline" className="border-border">
              Instant Poll
            </Button>
            <Button variant="outline" className="border-border">
              Scheduled Poll
            </Button>
            <Button className="ml-auto bg-foreground text-background hover:bg-foreground/90">
              Create
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Polls List */}
      <motion.div
        className="space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {polls.map((poll) => (
          <motion.div
            key={poll.id}
            variants={itemVariants}
            className="border border-border p-6 hover:border-foreground/50 transition-colors"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-lg font-medium text-foreground mb-2">
                  {poll.question}
                </h2>
                <div className="flex items-center gap-3">
                  <Badge variant={poll.status === "active" ? "default" : "secondary"}>
                    {poll.status === "active"
                      ? "Live"
                      : poll.status === "closed"
                      ? "Closed"
                      : "Upcoming"}
                  </Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {poll.closesIn || poll.startsIn}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {poll.totalVotes.toLocaleString()} votes
                  </span>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => setSelectedPoll(selectedPoll === poll.id ? null : poll.id)}
                className="text-xs font-medium text-foreground hover:opacity-70"
              >
                {selectedPoll === poll.id ? "Hide" : "View"} Analytics
              </motion.button>
            </div>

            {/* Options */}
            <div className="space-y-4 mb-6">
              {poll.options.map((option, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleVote(poll.id, idx)}
                        disabled={poll.voted || poll.status !== "active"}
                        className="h-4 w-4 border border-foreground rounded hover:bg-foreground/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      />
                      <label className="text-sm text-foreground cursor-pointer flex-1">
                        {option.text}
                      </label>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {option.percentage}% ({option.votes.toLocaleString()})
                    </span>
                  </div>
                  <div className="w-full bg-border h-1.5 overflow-hidden">
                    <motion.div
                      className="h-full bg-foreground"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.8, delay: idx * 0.1 }}
                      style={{ originX: 0 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Analytics */}
            {selectedPoll === poll.id && poll.demographics && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="pt-6 border-t border-border space-y-6"
              >
                <div>
                  <h4 className="text-sm font-medium mb-3">By Region</h4>
                  <div className="space-y-2">
                    {poll.demographics.region.map((region, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span>{region.label}</span>
                        <span className="text-muted-foreground">{region.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-3">By Persona</h4>
                  <div className="space-y-2">
                    {poll.demographics.persona.map((persona, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span>{persona.label}</span>
                        <span className="text-muted-foreground">{persona.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
